import { t } from "../i18n";
import { castName, SPRITE } from "../art/cast";
import type { Ctx } from "../art/pixel";
import { $, h } from "../lib/dom";
import { fitCanvas, updateCamera } from "./camera";
import { drawPip } from "./cinema";
import { facingInteract, P, updatePlayerIntent } from "./player";
import { setPadHint } from "./touchpad";
import { L, S } from "./state";
import type { Dir, Ent, Move } from "./types";
import { drawBubbles } from "./waits";
import { bg, rebuildDyn, T } from "./world";
import { drawZoom, isZoomOpen } from "./zoom";

const cv = $<HTMLCanvasElement>("#cv");
const ctxOrNull = cv.getContext("2d");
if (!ctxOrNull) throw new Error("2d context unavailable");
const ctx = ctxOrNull;
ctx.imageSmoothingEnabled = false;

/** ms per tile step. */
const STEP_MS = 120;

interface Mover {
  x: number;
  y: number;
  fx: number;
  fy: number;
  moving: Move | null;
}

function updateMover(m: Mover, now: number): void {
  if (!m.moving) return;
  const k = Math.min(1, (now - m.moving.start) / STEP_MS);
  m.fx = m.moving.from[0] + (m.moving.to[0] - m.moving.from[0]) * k;
  m.fy = m.moving.from[1] + (m.moving.to[1] - m.moving.from[1]) * k;
  if (k >= 1) {
    m.moving = null;
    m.fx = m.x;
    m.fy = m.y;
  }
}

/** Draw a chibi sprite at fractional tile coordinates. */
export function drawChar(
  c: Ctx,
  k: string,
  fx: number,
  fy: number,
  dir: Dir,
  walking: boolean,
  now: number,
  fx2?: Ent["fx2"],
): void {
  const sp = SPRITE[k];
  if (!sp) return;
  const f = walking && Math.floor(now / 140) % 2 ? 1 : 0;
  const img = dir === "up" ? (f ? sp.b1 : sp.b0) : f ? sp.f1 : sp.f0;
  const ox = fx2 === "shake" ? (Math.floor(now / 60) % 2 ? 1 : -1) : 0;
  const bob = walking && f ? -1 : 0;
  const px = Math.round(fx * T);
  const py = Math.round(fy * T);
  c.fillStyle = "rgba(0,0,0,.18)";
  c.fillRect(px + 3, py + 13, 10, 3);
  c.save();
  if (fx2 === "down") {
    c.translate(px + 8, py + 10);
    c.rotate(-Math.PI / 2);
    c.drawImage(sp.f0, -8, -8);
  } else if (dir === "left") {
    c.translate(px + T + ox, 0);
    c.scale(-1, 1);
    c.drawImage(img, 0, py - 4 + bob);
  } else c.drawImage(img, px + ox, py - 4 + bob);
  c.restore();
}

/** Display name for an interaction id (hint bar & help legend). */
export function interactName(id: string): string {
  return L.labels?.[id] ?? L.objects.find((o) => (o.act ?? o.id) === id && o.label)?.label ?? (castName(id) || id);
}

export const clerkSprite = (): string => L.clerkSprite?.() ?? "clerk";

/** Sprites sorted for painter's order (player included). */
export function sceneCharacters(): {
  k: string;
  fx: number;
  fy: number;
  dir: Dir;
  walking: boolean;
  fx2?: Ent["fx2"];
  me?: true;
}[] {
  const list: ReturnType<typeof sceneCharacters> = [
    { k: clerkSprite(), fx: P.fx, fy: P.fy, dir: P.dir, walking: !!P.moving, me: true },
  ];
  for (const e of S.ents)
    if (!e.hidden)
      list.push({
        k: e.sprite ?? e.k,
        fx: e.fx,
        fy: e.fy,
        dir: e.face && !e.moving ? e.face : e.dir,
        walking: !!e.moving || !!e.working,
        fx2: e.fx2,
      });
  return list.sort((a, b) => a.fy - b.fy);
}

function stepEntities(now: number): void {
  let dirty = false;
  for (const e of S.ents) {
    updateMover(e, now);
    if (!e.moving) {
      const next = e.path.shift();
      if (next) {
        const [nx, ny] = next;
        if (nx === P.x && ny === P.y && !e.path.length) e.path = [];
        else {
          e.dir = nx > e.x ? "right" : nx < e.x ? "left" : ny > e.y ? "down" : "up";
          e.moving = { from: [e.x, e.y], to: [nx, ny], start: now };
          e.x = nx;
          e.y = ny;
          dirty = true;
        }
      }
    }
    if (!e.moving && !e.path.length && e.onArrive) {
      const f = e.onArrive;
      e.onArrive = null;
      f();
    }
  }
  if (dirty) rebuildDyn();
}

function drawFacingHint(now: number): void {
  const hint = $("#hint");
  const near = !P.moving && !S.cine && !isZoomOpen() ? facingInteract() : null;
  if (!near) {
    hint.style.display = "none";
    setPadHint("");
    return;
  }
  const [id, mx, my] = near;
  const b = Math.floor(now / 300) % 2;
  const x = mx * T + 4;
  const y = my * T - 12 - b;
  const en = S.ents.find((e) => !e.hidden && e.x === mx && e.y === my);
  if (!(en && (S.flags[en.id] || S.speaking === en.id))) {
    ctx.fillStyle = "#1b1426";
    ctx.fillRect(x - 1, y + 1, 10, 2);
    ctx.fillRect(x, y + 3, 8, 2);
    ctx.fillRect(x + 1, y + 5, 6, 2);
    ctx.fillRect(x + 2, y + 7, 4, 2);
    ctx.fillStyle = "#ffd447";
    ctx.fillRect(x, y + 2, 8, 1);
    ctx.fillRect(x + 1, y + 3, 6, 2);
    ctx.fillRect(x + 2, y + 5, 4, 2);
    ctx.fillRect(x + 3, y + 7, 2, 1);
  }
  const name = interactName(id);
  hint.replaceChildren(h("b", { text: t("stage.hintKey") }), `${t("log.sep")}${name}`);
  setPadHint(name);
  hint.style.display = "";
}

function frame(now: number): void {
  requestAnimationFrame(frame);
  if (!S.live || !S.started) return;
  updateMover(P, now);
  if (!P.moving && !S.ended && !S.cine && !isZoomOpen()) updatePlayerIntent();
  stepEntities(now);
  ctx.drawImage(bg, 0, 0);
  L.drawBelow?.(ctx, now);
  for (const c of sceneCharacters()) drawChar(ctx, c.k, c.fx, c.fy, c.dir, c.walking, now, c.fx2);
  L.drawAbove?.(ctx, now);
  drawBubbles(ctx, now);
  drawFacingHint(now);
  updateCamera();
  drawPip(now);
  drawZoom(now);
}

export function startRenderLoop(): void {
  // The stage box changes with rotation, the mobile toolbar and the side panel, not only window resizes.
  new ResizeObserver(() => {
    if (S.live && S.started) fitCanvas();
  }).observe($("#stage"));
  requestAnimationFrame(frame);
}
